import React from 'react'
import { connect } from 'react-redux'
import { focusElement } from '@chegg-wt/core/utils/focusElement'
import { useModalBehavior } from '@chegg-ui/modal/'
import { useScreenSize } from '@chegg-wt/core/hooks/useScreenSize'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  modalTypeSelector,
  modalDataSelector,
} from '@chegg-wt/core/state/modals/selectors'
import CopyCitationInterstitial from '@chegg-wt/core/components/CopyCitationInterstitial'
import { colors } from '@chegg-ui/theme'
import reloadAds from '@chegg-wt/core/utils/ads'
import { layoutNameFromWidth } from '@chegg-wt/core/utils/adframe'
import { adzoneSBMSelector } from '@chegg-wt/core/state/app/selectors'
import { getCookie, Cookies } from '@chegg-wt/core/utils/cookies'
import { CM } from '@chegg-wt/core/constants/site'
import { Config } from '@chegg-wt/core/config'
import {
  STOP_AD_RELOAD_CM_TEST_CELL_VARIABLE_REGEX,
} from '@chegg-wt/core/state/optimizely/constants'
import { doesStringMatchesRegex } from '@chegg-wt/core/utils/regex'
import Modal from '..'
import {
  AdAndCopyContentWrapper,
  AdContainer,
  CloseBar,
  CopySuccessContainer,
  MainContainer,
} from './styled'
import {
  AD_CONTAINER_TEST_ID,
  AD_ID,
  CLOSE,
  CLOSE_BAR_TEST_ID,
  COPY_SUCCESS_INTERSTITIAL_MODAL_ID,
} from './constants'

export interface Props {
  modalData: {
    text: string
    shouldShowWarning: boolean
    returnFocusSelector: string
  }
  closeModal: (name: string) => void
  modalName: string
  adzone: string
}

export const CopySuccessInterstitialModal: React.FunctionComponent<Props> = ({
  modalData,
  closeModal,
  modalName,
  adzone,
}) => {
  const { returnFocusSelector } = modalData
  const layout = layoutNameFromWidth(window.innerWidth)
  const hasRecentlyViewedInterstitialAd =
    getCookie(Cookies.SbmViewedCopyInterstitial) === 'true'

  const { appName } = Config

  const { isEnabled, variables } = { 
    isEnabled: false, 
    variables: { adConfigVersion: '' } 
  }

  const onClose = () => {
    closeModal(modalName)
    focusElement(returnFocusSelector, 0)
  }

  const onCloseBarClick = () => {
    closeModal(modalName)
    if (appName === CM) {
      if (
        !(
          isEnabled &&
          doesStringMatchesRegex(
            STOP_AD_RELOAD_CM_TEST_CELL_VARIABLE_REGEX,
            variables.adConfigVersion as string
          )
        )
      ) {
        reloadAds(adzone, 'pagechange', layout)
      }
    } else {
      reloadAds(adzone, 'pagechange', layout)
    }
  }
  const onCloseButtonClick = () => {
    focusElement(returnFocusSelector, 0)
    if (appName === CM) {
      if (
        !(
          isEnabled &&
          doesStringMatchesRegex(
            STOP_AD_RELOAD_CM_TEST_CELL_VARIABLE_REGEX,
            variables.adConfigVersion as string
          )
        )
      ) {
        reloadAds(adzone, 'pagechange', layout)
      }
    } else {
      reloadAds(adzone, 'pagechange', layout)
    }
  }

  const screenSize = useScreenSize()
  const { modalRef, onModalKeyDown } = useModalBehavior({
    isOpen: true,
    onClose,
    id: COPY_SUCCESS_INTERSTITIAL_MODAL_ID,
  })
  React.useEffect(() => {
    modalRef.current?.focus()
  }, [modalRef])

  React.useEffect(() => {
    if (!hasRecentlyViewedInterstitialAd) {
      reloadAds(adzone, 'pagechange', `${layout}-copy` as any)
    }
  }, [layout, adzone, hasRecentlyViewedInterstitialAd])

  return hasRecentlyViewedInterstitialAd || ['PHONE'].includes(screenSize) ? (
    <Modal
      ref={modalRef}
      onKeyDown={onModalKeyDown}
      closeButton
      enableScroll={false}
      fullMobileModal
      outsideClick
      showModal
      maxWidth="400px"
      showBackdrop
      onClose={onCloseButtonClick}
      id={COPY_SUCCESS_INTERSTITIAL_MODAL_ID}
    >
      <CopyCitationInterstitial />
    </Modal>
  ) : (
    <Modal
      ref={modalRef}
      onKeyDown={onModalKeyDown}
      closeButton
      closeButtonColor={colors.white}
      topSpace="5px"
      enableScroll={false}
      noPadding
      fullMobileModal
      outsideClick
      showModal
      maxWidth="100vw"
      showBackdrop
      onClose={onCloseButtonClick}
      id={COPY_SUCCESS_INTERSTITIAL_MODAL_ID}
    >
      <MainContainer>
        <CloseBar onClick={onCloseBarClick} data-test={CLOSE_BAR_TEST_ID}>
          {CLOSE}
        </CloseBar>
        <AdAndCopyContentWrapper>
          <AdContainer id={AD_ID} data-test={AD_CONTAINER_TEST_ID} />
          <CopySuccessContainer>
            <CopyCitationInterstitial />
          </CopySuccessContainer>
        </AdAndCopyContentWrapper>
      </MainContainer>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  modalData: modalDataSelector(state),
  modalName: modalTypeSelector(state),
  adzone: adzoneSBMSelector(state),
})

const mapDispatchToProps = {
  closeModal,
}

export default connect(mapStateToProps, mapDispatchToProps)(CopySuccessInterstitialModal)
